import { CameraController, CameraError } from '../camera/CameraController';
import { SPECIAL_LENSES } from '../config/appearance';
import { track } from '../analytics/events';
import { btn, h, icon } from '../ui/icons';
import { SUNSET_DISCS } from '../ui/AppearanceControls';
import { words } from '../ui/words';
import { LensRenderer } from './LensRenderer';
import { LENS_VIEW_DENSITIES, lensFilter, lensViewDensity, type LensFilter, type RGB } from './lensFilter';

export interface LensViewOptions {
  /** Sunset lens shown first (falls back to « Sunset »). */
  lens: string;
  /** Try-on density; snapped to Light 30 / Dark 85. */
  density: number;
  /** Lens or intensity chosen in the view (kept by the try-on when it resumes). */
  onSelect?: (lens: string, density: number) => void;
  /** The customer left the view (close button, Escape, error screen). */
  onClose?: () => void;
}

/** The hint is shown once per page. */
let hintShown = false;

const reducedMotion = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A110T/A110U — « Voir le paysage » : the rear camera, full screen, seen through a
 * Sunset lens. Opened from the try-on (Couleurs → Sunset); the try-on pauses
 * its front camera meanwhile and resumes with the same settings afterwards.
 * Nothing is recorded or sent: the image only goes from the camera to the screen.
 */
export class LensView {
  /** Phones and tablets (touch screen with a camera API, secure page). */
  static isSupported(): boolean {
    if (typeof window === 'undefined' || !window.isSecureContext) return false;
    if (!CameraController.isSupported()) return false;
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  }

  readonly root = h('div', 'mt-lv');
  private readonly video = document.createElement('video');
  private readonly canvas = h('canvas', 'mt-lv-canvas');
  private readonly fallbackTint = h('div', 'mt-lv-tint');
  private readonly status = h('p', 'mt-lv-status');
  private readonly name = h('span', 'mt-lv-name');
  private readonly hint = h('p', 'mt-lv-hint');
  private readonly sheet = h('div', 'mt-lv-sheet');
  private readonly errorBox = h('div', 'mt-lv-error');
  private readonly errorText = h('p', 'mt-lv-error-text');
  private readonly retryBtn: HTMLButtonElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly compareBtn: HTMLButtonElement;
  private readonly discButtons: HTMLButtonElement[] = [];
  private readonly intensityButtons: HTMLButtonElement[] = [];
  private readonly camera: CameraController;
  private renderer: LensRenderer | null = null;
  private lens: string;
  private density: number;
  private target: LensFilter;
  private shown: [RGB, RGB, RGB];
  private mixTarget = 1;
  private mixShown = 1;
  private mirror = false;
  private rafId = 0;
  private lastFrameTime = -1;
  private lastTick = 0;
  private openedAt = 0;
  private hintTimer = 0;
  private closed = false;
  private readonly inerted: HTMLElement[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private readonly options: LensViewOptions;

  constructor(private readonly host: HTMLElement, options: LensViewOptions) {
    this.options = options;
    this.lens = SPECIAL_LENSES.some(l => l.id === options.lens) ? options.lens : 'sunset';
    this.density = lensViewDensity(options.density);
    this.target = lensFilter(this.lens, this.density);
    this.shown = [...this.target.stops];
    this.camera = new CameraController(this.video);

    const w = words();
    const lw = w.lensView;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', lw.title);
    this.video.className = 'mt-lv-video';
    this.video.setAttribute('aria-hidden', 'true');
    this.canvas.setAttribute('aria-hidden', 'true');
    this.fallbackTint.setAttribute('aria-hidden', 'true');
    this.fallbackTint.hidden = true;
    this.status.setAttribute('role', 'status');
    this.status.textContent = lw.opening;

    // Top: lens name chip.
    const top = h('div', 'mt-lv-top');
    this.name.setAttribute('aria-live', 'polite');
    top.append(this.name);
    // A110U: leaving the view is a camera icon (« back to the front camera »), in the
    // footer, where « Voir le paysage » was in the Colours panel: the same spot toggles.
    this.closeBtn = btn('mt-lv-front');
    this.closeBtn.setAttribute('aria-label', lw.front);
    this.closeBtn.title = lw.front;
    this.closeBtn.append(icon('cameraFront'));
    this.closeBtn.onclick = () => this.close();

    this.hint.hidden = true;
    const hintText = h('span', 'mt-lv-hint-main', lw.hint);
    const hintNote = h('span', 'mt-lv-hint-note', lw.note);
    this.hint.append(hintText, hintNote);

    // Bottom sheet: the 11 Sunset lenses, intensity, and « Sans verre ».
    const discs = h('div', 'mt-discs mt-discs--sun mt-lv-discs');
    discs.setAttribute('role', 'group');
    discs.setAttribute('aria-label', `${w.sections[2]} · ${SPECIAL_LENSES.length}`);
    for (const lens of SPECIAL_LENSES) {
      const [topColour, bottomColour] = SUNSET_DISCS[lens.id] ?? [lens.top, lens.bottom];
      const b = btn('mt-lens-disc');
      b.dataset.id = lens.id;
      const swatch = h('span', 'mt-disc');
      swatch.style.background = `linear-gradient(180deg,${topColour},${bottomColour})`;
      swatch.setAttribute('aria-hidden', 'true');
      b.append(swatch, h('span', 'mt-disc-label', lens.label));
      b.onclick = () => this.select(lens.id, this.density);
      discs.append(b);
      this.discButtons.push(b);
    }
    const foot = h('div', 'mt-lv-foot');
    const intensity = h('div', 'mt-seg mt-lv-int');
    intensity.setAttribute('role', 'group');
    intensity.setAttribute('aria-label', w.sections[3]);
    LENS_VIEW_DENSITIES.forEach((value, i) => {
      const b = btn('mt-seg-item mt-lv-int-item', w.intensity[i]);
      b.dataset.density = String(value);
      b.onclick = () => this.select(this.lens, value);
      intensity.append(b);
      this.intensityButtons.push(b);
    });
    this.compareBtn = btn('mt-lv-compare');
    this.compareBtn.setAttribute('aria-label', lw.compareAria);
    this.compareBtn.append(icon('compare'), h('span', 'mt-lv-compare-label', lw.compare));
    this.installCompare();
    // Footer: intensity (left), « Sans verre » (centre), back to the front camera (right).
    foot.append(intensity, this.compareBtn, this.closeBtn);
    this.sheet.append(discs, foot);

    // Error screen (no rear camera, permission refused).
    this.errorBox.hidden = true;
    this.errorBox.setAttribute('role', 'alert');
    this.retryBtn = btn('mt-btn mt-lv-retry', lw.retry);
    this.retryBtn.onclick = () => void this.startCamera();
    const back = btn('mt-btn mt-btn-primary mt-lv-back', lw.back);
    back.onclick = () => this.close();
    this.errorBox.append(this.errorText, this.retryBtn, back);

    this.root.append(this.video, this.canvas, this.fallbackTint, this.status, top, this.hint, this.sheet, this.errorBox);
    this.root.addEventListener('keydown', this.onKey);
    this.refresh();
  }

  get currentLens(): string { return this.lens; }
  get currentDensity(): number { return this.density; }

  /** Shows the view and starts the rear camera. */
  async open(): Promise<void> {
    this.openedAt = performance.now();
    // Everything behind the view is out of reach (screen readers, keyboard).
    for (const child of Array.from(this.host.children)) {
      if (child instanceof HTMLElement && !child.inert) { child.inert = true; this.inerted.push(child); }
    }
    this.host.append(this.root);
    this.closeBtn.focus({ preventScroll: true });
    this.renderer = LensRenderer.create(this.canvas);
    if (!this.renderer) this.useFallback();
    this.resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.resize()) : null;
    this.resizeObserver?.observe(this.root);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.resize();
    track('tryon_lensview_open', { lens: this.lens });
    await this.startCamera();
  }

  private async startCamera(): Promise<void> {
    if (this.closed) return;
    const lw = words().lensView;
    this.errorBox.hidden = true;
    this.status.hidden = false;
    this.status.textContent = lw.opening;
    try {
      await this.camera.start({ facingMode: 'environment', idealLongSide: 1920, idealFrameRate: 30 });
    } catch (err) {
      if (this.closed) return;
      const code = err instanceof CameraError ? err.code : 'unknown';
      track('tryon_error', { code: `lensview_${code}` });
      this.status.hidden = true;
      this.errorText.textContent = code === 'denied' ? lw.denied : lw.error;
      this.retryBtn.hidden = code === 'insecure_context';
      this.errorBox.hidden = false;
      if (this.retryBtn.hidden) this.errorBox.querySelector<HTMLButtonElement>('.mt-lv-back')?.focus();
      else this.retryBtn.focus();
      return;
    }
    if (this.closed) { this.camera.stop(); return; }
    // A front-facing camera (no rear camera on this device) is shown as a mirror.
    this.mirror = this.camera.getSettings()?.facingMode === 'user';
    this.root.classList.toggle('mt-lv--mirror', this.mirror);
    this.status.hidden = true;
    this.lastFrameTime = -1;
    this.startLoop();
    if (!hintShown) {
      hintShown = true;
      this.hint.hidden = false;
      this.hintTimer = window.setTimeout(() => this.hideHint(), 4500);
    }
  }

  private hideHint(): void {
    window.clearTimeout(this.hintTimer);
    if (this.hint.hidden) return;
    if (reducedMotion()) { this.hint.hidden = true; return; }
    this.hint.classList.add('mt-lv-hint--out');
    window.setTimeout(() => { this.hint.hidden = true; this.hint.classList.remove('mt-lv-hint--out'); }, 400);
  }

  /** Lens and/or intensity change (from the discs or the intensity buttons). */
  select(lens: string, density: number): void {
    if (!SPECIAL_LENSES.some(l => l.id === lens)) return;
    const d = lensViewDensity(density);
    if (lens === this.lens && d === this.density) return;
    this.lens = lens;
    this.density = d;
    this.target = lensFilter(lens, d);
    if (reducedMotion()) this.shown = [...this.target.stops];
    this.refresh();
    this.options.onSelect?.(lens, d);
  }

  private refresh(): void {
    const w = words();
    const label = SPECIAL_LENSES.find(l => l.id === this.lens)?.label ?? this.lens;
    const intensity = w.intensity[LENS_VIEW_DENSITIES.indexOf(this.density as typeof LENS_VIEW_DENSITIES[number])] ?? '';
    this.name.textContent = this.mixTarget === 0 ? w.lensView.compare : `${label} · ${intensity}`;
    this.discButtons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === this.lens)));
    this.intensityButtons.forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.density) === this.density)));
    this.root.dataset.lens = this.lens;
    this.root.dataset.density = String(this.density);
    if (!this.renderer) this.paintFallback();
  }

  // ------------------------------------------------------------ compare (press and hold)

  private setCompare(on: boolean): void {
    this.mixTarget = on ? 0 : 1;
    if (reducedMotion()) this.mixShown = this.mixTarget;
    this.compareBtn.setAttribute('aria-pressed', String(on));
    this.root.classList.toggle('mt-lv--bare', on);
    if (on) this.hideHint();
    this.refresh();
  }

  private installCompare(): void {
    const surface = (target: EventTarget | null) => target === this.canvas || target === this.video || target === this.fallbackTint || target === this.root;
    let pointer: number | null = null;
    const down = (e: PointerEvent) => {
      if (pointer !== null) return;
      pointer = e.pointerId;
      try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch { /* not capturable */ }
      this.setCompare(true);
    };
    const up = (e: PointerEvent) => {
      if (pointer !== e.pointerId) return;
      pointer = null;
      this.setCompare(false);
    };
    this.root.addEventListener('pointerdown', e => { if (surface(e.target) && this.status.hidden && this.errorBox.hidden) down(e); });
    this.compareBtn.addEventListener('pointerdown', e => { e.preventDefault(); down(e); });
    const targets: HTMLElement[] = [this.root, this.compareBtn];
    for (const el of targets) {
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      el.addEventListener('lostpointercapture', up);
    }
    // Keyboard: hold Space or Enter on the button.
    this.compareBtn.addEventListener('keydown', e => {
      if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); this.setCompare(true); }
    });
    this.compareBtn.addEventListener('keyup', e => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); this.setCompare(false); }
    });
    this.compareBtn.addEventListener('blur', () => { if (pointer === null) this.setCompare(false); });
    // A long press must not open the system menu or select text.
    this.root.addEventListener('contextmenu', e => e.preventDefault());
  }

  // ------------------------------------------------------------ rendering

  private startLoop(): void {
    if (this.rafId || this.closed || document.visibilityState !== 'visible') return;
    this.lastTick = performance.now();
    const loop = (now: number) => {
      this.rafId = requestAnimationFrame(loop);
      this.tick(now);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private stopLoop(): void {
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private tick(now: number): void {
    const dt = Math.min(100, now - this.lastTick);
    this.lastTick = now;
    // Smooth lens changes (≈ 0.25 s) and the comparison fade (≈ 0.15 s).
    const k = 1 - Math.exp(-dt / 70);
    const lerp = (a: number, b: number, f: number) => a + (b - a) * f;
    const t = this.target.stops;
    this.shown = [0, 1, 2].map(i => [0, 1, 2].map(c => lerp(this.shown[i]![c]!, t[i]![c]!, k))) as unknown as [RGB, RGB, RGB];
    this.mixShown = lerp(this.mixShown, this.mixTarget, 1 - Math.exp(-dt / 45));
    if (Math.abs(this.mixShown - this.mixTarget) < 0.004) this.mixShown = this.mixTarget;
    if (!this.renderer) return;
    if (this.renderer.lost) { this.renderer = null; this.useFallback(); return; }
    const frameTime = this.video.currentTime;
    const upload = frameTime !== this.lastFrameTime;
    this.lastFrameTime = frameTime;
    this.renderer.draw(this.video, { stops: this.shown, mix: this.mixShown, mirror: this.mirror, vignette: 0.28 }, upload);
  }

  private resize(): void {
    const r = this.root.getBoundingClientRect();
    this.renderer?.resize(r.width, r.height);
  }

  /** No WebGL: the video is shown as is, under a CSS tint (multiply). */
  private useFallback(): void {
    this.root.classList.add('mt-lv--css');
    this.fallbackTint.hidden = false;
    this.canvas.hidden = true;
    this.paintFallback();
  }

  /** CSS fallback: exactly the article treatment, the tint layer in multiply mode over the video. */
  private paintFallback(): void {
    this.fallbackTint.style.background = this.target.css;
    this.fallbackTint.style.opacity = this.mixTarget === 0 ? '0' : '1';
  }

  // ------------------------------------------------------------ lifecycle

  private readonly onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(); return; }
    if (e.key === 'Tab') {
      // Keep the focus inside the view.
      const focusables = Array.from(this.root.querySelectorAll<HTMLElement>('button')).filter(b => !b.closest('[hidden]') && !b.hidden);
      if (!focusables.length) return;
      const first = focusables[0]!, last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  private readonly onResize = () => this.resize();

  private readonly onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void this.video.play().catch(() => undefined);
      this.startLoop();
    } else {
      this.stopLoop();
      this.setCompare(false);
    }
  };

  /** The customer leaves the view: the try-on resumes (onClose). */
  close(): void {
    if (this.closed) return;
    track('tryon_lensview_close', { lens: this.lens, duration_ms: Math.round(performance.now() - this.openedAt) });
    this.destroy();
    this.options.onClose?.();
  }

  /** Stops the rear camera and removes the view, without callback (the try-on is closing). */
  destroy(): void {
    if (this.closed) return;
    this.closed = true;
    this.stopLoop();
    window.clearTimeout(this.hintTimer);
    this.camera.stop();
    this.renderer?.dispose();
    this.renderer = null;
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.root.removeEventListener('keydown', this.onKey);
    for (const el of this.inerted) el.inert = false;
    this.inerted.length = 0;
    this.root.remove();
  }
}
